import React, { ReactElement, useEffect, useState } from 'react';
import { css } from '@compiled/react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { token } from '@atlaskit/tokens';
import { Button, ButtonAppearance, Icon } from '@atlassian/learning-components';
import FeatureGates from '@atlaskit/feature-gate-js-client';

import { SubNavbarLink } from 'src/pages/App/children/SubNavbarLink';
import { useAtlassianUser } from 'src/hooks/useAtlassianUser';
import { ALM_INTEGRATION_FF, ENABLEMENT_ACADEMY_IN_ALP_FF, LandingType, NEW_TOP_NAVIGATION_FF } from 'src/constants';
import { sendUIEvent } from 'src/utils/analytics';
import { detectPage } from 'src/utils/detectPage';
import { OnDemandLearningDropdown } from 'src/pages/App/children/OnDemandLearningDropdown';
import { AdoptionDropdown } from 'src/pages/App/children/AdoptionDropdown';
import {
  getCatalogUrl,
  getCertificationUrl,
  getLandingUrl,
  getMyLearningUrl,
  MY_LEARNING_URL_PATH,
  withPrefix,
} from 'src/utils/getUrl';
import { MyLearningPageContentSections } from 'src/pages/MyLearningPage/constants';
import { OldSubNavbarDropdown } from 'src/pages/App/children/OldSubnavbarDropdown';
import { getTenant, TENANTS } from 'src/utils/tenant';

export const LearningSubNavbar = (): ReactElement | null => {
  const [isOnDemandMenuOpen, setIsOnDemandMenuOpen] = useState(false);
  const [isAdoptionMenuOpen, setIsAdoptionMenuOpen] = useState(false);
  const navigate = useNavigate();
  const { user } = useAtlassianUser();
  const [scrollProgress, setScrollProgress] = useState(0);

  const showNewTopNavigation = FeatureGates.checkGate(NEW_TOP_NAVIGATION_FF) ?? false;
  const almIntegrationEnabled = FeatureGates.checkGate(ALM_INTEGRATION_FF) ?? false;
  const enablementAcademyEnabled = FeatureGates.checkGate(ENABLEMENT_ACADEMY_IN_ALP_FF);

  const location = useLocation();
  const isSelected = (path: string): boolean => location.pathname === path;
  const currentTenant = getTenant();

  useEffect(() => {
    const handleScroll = (): void => {
      setScrollProgress(window.scrollY);
    };

    window.addEventListener('scroll', handleScroll);

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  if (currentTenant === TENANTS.ENABLEMENT_ACADEMY && enablementAcademyEnabled) {
    return null;
  }

  const handleMyLearningClick = (event?: React.MouseEvent): void => {
    event?.preventDefault();

    user &&
      sendUIEvent(
        'learningApp',
        'button',
        'clicked',
        'myLearningButton',
        {
          navigatedFrom: detectPage(location.pathname),
        },
        user.account_id,
      );

    const myLearningUrl = almIntegrationEnabled
      ? `/${MY_LEARNING_URL_PATH}/on-demand`
      : getMyLearningUrl(MyLearningPageContentSections.IN_PROGRESS);

    navigate(myLearningUrl);
  };

  const handleDropdownItemClick = (): void => {
    setIsOnDemandMenuOpen(false);
    sendUIEvent(
      'learningApp',
      'subNavbarDropdown',
      'clicked',
      'subNavbarDropdownItem',
      {
        navigatedFrom: detectPage(location.pathname),
      },
      user?.account_id,
    );
  };

  const dynamicStyles = {
    transform: `translateY(-${Math.min(scrollProgress, 68)}px)`,
  };

  const handleLearningClick = (): void => {
    navigate('/');
  };

  return (
    <nav
      id="subnavbar"
      data-testid="subnavbar"
      css={[containerStyles, scrollProgress > 0 && scrolledStyles, disableDynamicStylesOnTable]}
      style={dynamicStyles}
      aria-label="learning"
    >
      <div css={contentStyles}>
        <div css={navigationContainerStyles}>
          <p
            role="button"
            aria-label="home-page-link"
            tabIndex={0}
            css={headingStyles}
            onClick={handleLearningClick}
            onKeyDown={handleLearningClick}
          >
            Learning
          </p>
          <ul css={contentListStyles} aria-label="learning navigation">
            <li
              css={contentItemStyles}
              onMouseEnter={() => setIsOnDemandMenuOpen(true)}
              onMouseLeave={() => setIsOnDemandMenuOpen(false)}
            >
              {showNewTopNavigation ? (
                <OnDemandLearningDropdown
                  onDropdownItemClick={handleDropdownItemClick}
                  setIsOnDemandMenuOpen={setIsOnDemandMenuOpen}
                  isMenuOpen={isOnDemandMenuOpen}
                />
              ) : (
                <OldSubNavbarDropdown onDropdownItemClick={handleDropdownItemClick} />
              )}
            </li>
            <li css={contentItemStyles}>
              <SubNavbarLink
                linkName="Live training"
                linkPath={getLandingUrl(LandingType.LIVE_TRAINING)}
                isSelected={isSelected(getLandingUrl(LandingType.LIVE_TRAINING))}
              />
            </li>
            <li css={contentItemStyles}>
              <SubNavbarLink
                linkName="Credentials"
                linkPath={getCertificationUrl()}
                isSelected={isSelected(getCertificationUrl())}
              />
            </li>
            <li css={contentItemStyles}>
              <AdoptionDropdown isOpen={isAdoptionMenuOpen} onToggle={setIsAdoptionMenuOpen} />
            </li>
          </ul>
        </div>
        <div css={rightButtonsContainerStyles}>
          {showNewTopNavigation && (
            <Link aria-label="Search catalog" css={searchButtonStyles} to={getCatalogUrl()}>
              <Icon type="search" size="medium" />
            </Link>
          )}
          {user && (
            <Button
              label="My learning"
              appearance={ButtonAppearance.SECONDARY}
              onClick={(event) => handleMyLearningClick(event)}
              target="_blank"
              href={withPrefix(
                almIntegrationEnabled
                  ? `/${MY_LEARNING_URL_PATH}/on-demand`
                  : getMyLearningUrl(MyLearningPageContentSections.IN_PROGRESS),
              )}
            />
          )}
        </div>
      </div>
    </nav>
  );
};

const disableDynamicStylesOnTable = css({
  '@media (max-width: 1280px)': {
    transform: 'unset !important',
  },
});

const scrolledStyles = css({
  zIndex: 200,
});

const containerStyles = css({
  position: 'fixed',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: '100%',
  height: '68px',
  backgroundColor: token('color.background.input'),
  zIndex: 6,
  top: '68px',
  padding: '0 16px',

  '@media (max-width: 1279px)': {
    display: 'none',
  },
});

const contentStyles = css({
  width: '100%',
  maxWidth: '1232px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',

  '@media (min-width: 1280px)': {
    width: '1232px',
  },
});

const navigationContainerStyles = css({
  display: 'flex',
  alignItems: 'center',
});

const headingStyles = css({
  textAlign: 'center',
  color: token('color.text'),
  fontFamily: 'Charlie Text, sans-serif',
  fontSize: '20px',
  fontWeight: 600,
  lineHeight: '20px',
  letterSpacing: '0.05px',
  marginTop: '8px !important',

  '&:hover': {
    cursor: 'pointer',
  },
});

const contentListStyles = css({
  display: 'flex',
  alignItems: 'center',
  paddingLeft: 0,
  marginTop: '8px',
  marginLeft: token('space.300'),
  listStyle: 'none',
});

const contentItemStyles = css({
  height: '56px',
  display: 'flex',
  alignItems: 'center',
  padding: `0 ${token('space.150')}`,
  marginTop: '0',
  marginLeft: token('space.100'),
  textAlign: 'center',
  color: token('color.text'),
  fontFamily: 'Charlie Text, sans-serif',
  fontSize: '16px',
  fontWeight: 400,
  lineHeight: '20px',
  letterSpacing: '-0.084px',
  position: 'relative',

  '&:first-of-type': {
    marginLeft: 0,
    cursor: 'pointer',
  },
});

const rightButtonsContainerStyles = css({
  display: 'flex',
  alignItems: 'center',
  gap: token('space.200'),
});

const searchButtonStyles = css({
  display: 'inline-block',
  cursor: 'pointer',
  color: token('color.icon'),

  '&:hover span': {
    color: `${token('color.link')} !important`,
  },
});
